/* RESPONSIBLE TEAM: team-reporting */
import { isEmpty, isPresent } from '@ember/utils';
import { first, flatten } from 'underscore';
import Admin from 'embercom/models/admin';
import Role from 'embercom/models/role';
import { isValidEmail } from 'embercom/lib/email';

export type Entity = Admin | Role;

/**
 * Remove accents from a string for accent-insensitive search
 * normalize("NFD"): breaks accented characters into base characters and diacritical marks
 * .replace(/[\u0300-\u036f]/g, ""): removes the diacritical marks
 */
export function removeAccents(name: string): string {
  return name.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/**
 * Compare two strings in a case-insensitive and accent-insensitive way
 */
export function compareWithoutAccents(value: string, target: string): boolean {
  let cleanedValue = value.toLowerCase();
  let cleanedTarget = target.toLowerCase();
  return removeAccents(cleanedValue).includes(removeAccents(cleanedTarget));
}

export function isTeammate(entity: Entity): boolean {
  return entity instanceof Admin;
}

export function getRoleSize(entity: Entity): number {
  if (entity instanceof Role) {
    let perRoleSize = Role.adminPerRole();
    return perRoleSize[entity.id] ?? 0;
  }
  return 0;
}

/**
 * Filter available teammates excluding already selected ones
 */
export function getAvailableTeammates(
  allTeammates: Admin[],
  selectedTeammates: Admin[],
  excludedIds: Set<string> = new Set(),
): Admin[] {
  let selectedIds = new Set(selectedTeammates.map((teammate) => teammate.id));
  return allTeammates.reject(
    (admin: Admin) => selectedIds.has(admin.id) || excludedIds.has(admin.id),
  );
}

/**
 * Filter available roles excluding already selected ones
 */
export function getAvailableRoles(
  allRoles: Role[],
  selectedRoles: Role[],
  excludedIds: Set<string> = new Set(),
): Role[] {
  let selectedIds = new Set(selectedRoles.map((role) => role.id));
  return allRoles.reject(
    (role) => selectedIds.has(role.id) || excludedIds.has(role.id) || getRoleSize(role) === 0,
  );
}

export function getSuggestedTeammates(availableTeammates: Admin[], userInput: string): Admin[] {
  if (isEmpty(userInput)) {
    return availableTeammates;
  }
  return availableTeammates.filter((admin: Admin) => compareWithoutAccents(admin.name, userInput));
}

export function getSuggestedRoles(availableRoles: Role[], userInput: string): Role[] {
  let roles: Role[] = isEmpty(userInput)
    ? availableRoles
    : availableRoles.filter((role: Role) => compareWithoutAccents(role.name, userInput));

  return roles.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
}

export function getSuggestedEntities(
  suggestedRoles: Role[],
  suggestedTeammates: Admin[],
): Entity[] {
  return flatten([suggestedRoles, suggestedTeammates]);
}

export function selectEntity(
  selectedEntities: Entity[],
  entity: Entity,
): { entities: Entity[]; lastAdded?: Entity } {
  if (entity) {
    return { entities: [...selectedEntities, entity], lastAdded: entity };
  }
  return { entities: selectedEntities, lastAdded: undefined };
}

export function removeEntity(selectedEntities: Entity[], entityToRemove: Entity): Entity[] {
  if (entityToRemove instanceof Admin) {
    return selectedEntities.filter(
      (entity) => !(entity.id === entityToRemove.id && entity instanceof Admin),
    );
  } else if (entityToRemove instanceof Role) {
    return selectedEntities.filter(
      (entity) => !(entity.id === entityToRemove.id && entity instanceof Role),
    );
  }
  return selectedEntities;
}

export function handleInput(
  popover: { hide: () => void; show: () => void },
  event: InputEvent & { target: HTMLInputElement },
): string {
  let userInput = event.target.value;
  if (isEmpty(userInput)) {
    popover.hide();
  } else {
    popover.show();
  }
  return userInput;
}

/**
 * Handle Enter key to select first suggested entity or add email
 */
export function handleEnter(
  popover: { hide: () => void },
  userInput: string,
  suggestedEntities: Entity[],
  onSelectEntity: (entity: Entity) => void,
  onAddEmail?: (email: string) => void,
): { shouldClearInput: boolean } {
  // If email support is enabled and input is a valid email, add it
  if (onAddEmail && isPresent(userInput) && isValidEmail(userInput.trim())) {
    onAddEmail(userInput.trim());
    popover.hide();
    return { shouldClearInput: true };
  }

  // Otherwise, select first suggested entity
  let selectedEntity = first(suggestedEntities);
  if (selectedEntity && isPresent(userInput)) {
    onSelectEntity(selectedEntity);
    popover.hide();
    return { shouldClearInput: true };
  }

  return { shouldClearInput: false };
}

/**
 * Handle Backspace key to remove last selected item
 */
export function handleBackspace(
  userInput: string,
  selectedEntities: Entity[],
  selectedEmails: string[] = [],
): { entities: Entity[]; emails: string[] } {
  if (isEmpty(userInput)) {
    if (selectedEmails.length > 0) {
      return {
        entities: selectedEntities,
        emails: selectedEmails.slice(0, -1),
      };
    } else if (selectedEntities.length > 0) {
      return {
        entities: selectedEntities.slice(0, selectedEntities.length - 1),
        emails: selectedEmails,
      };
    }
  }
  return { entities: selectedEntities, emails: selectedEmails };
}

export function removeEmail(selectedEmails: string[], emailToRemove: string): string[] {
  return selectedEmails.filter((e) => e !== emailToRemove);
}

/**
 * Extract entities (admins and roles) from schedule recipients
 */
export function extractEntitiesFromRecipients(
  recipients: { userIds?: number[]; roles?: number[]; emails?: string[] } | undefined,
  availableAdmins: Admin[],
  availableRoles: Role[],
): { entities: Entity[]; emails: string[] } {
  if (!recipients) {
    return { entities: [], emails: [] };
  }

  let admins: Entity[] = [];
  if (recipients.userIds && recipients.userIds.length > 0) {
    admins = recipients.userIds
      .map((userId: number) => availableAdmins.find((a) => parseInt(a.id, 10) === userId))
      .filter((admin): admin is Admin => admin !== undefined);
  }

  let roles: Entity[] = [];
  if (recipients.roles && recipients.roles.length > 0) {
    roles = recipients.roles
      .map((roleId) => availableRoles.find((r) => parseInt(r.id, 10) === roleId))
      .filter((role): role is Role => role !== undefined);
  }

  return {
    entities: [...admins, ...roles],
    emails: recipients.emails ?? [],
  };
}
